/* Copyright (c) Trimble Inc. All rights reserved. */

import {
    useMemo,
    useReducer,
    useState,
    useCallback,
    useEffect,
    useRef
  } from 'react';
  import type { Reducer } from 'react';
  import { ACTION_STATUS } from './AsyncAction.type';
  import type { AsyncActionState } from './AsyncAction.type';
  import {
    defaultState,
    AsyncActionReducer
  } from './AsyncActionReducer';
  import type {
     AsyncStateActionType
  } from './AsyncActionReducer';
  import {
    of,
    Subject
  } from 'rxjs';
  import type {
     Observable
  } from 'rxjs';
import { SubSink } from 'subsink';
import { catchError, finalize, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
  
  export type AsyncOptions = {
    map?: (value: any) => void;
    error?: (error: any) => void;
    isControlled?: boolean;
  };
  
  export type AsyncActionInstance<U> = {
    state: AsyncActionState<U | null>;
    subscribe: (isStreaming?: boolean) => () => void;
    reset: () => void;
  };
  
  export const initialAsyncState: AsyncActionState = {
    status: 'INIT',
    init: true,
    loading: false,
    intermediate: false,
    loaded: false,
    error: undefined,
  };
  export function useAsyncAction<T, U = T>(
    observerFn$: () => Observable<T>,
    options?: AsyncOptions
  ): AsyncActionInstance<U> {
    // For handling smooth unsubscribing
    const subSink = useMemo(() => new SubSink(), []);
    const [unsubscriber$] = useState(new Subject());
    const [state, dispatch] = useReducer<
      Reducer<AsyncActionState, AsyncStateActionType>
    >(AsyncActionReducer, { ...defaultState });
  
    // For Observer Mapping to set status
    const observerFnRef = useRef(observerFn$);
    useEffect(() => {
      if (options?.isControlled) return;
      observerFnRef.current = observerFn$;
    }, [observerFn$]);
  
    useEffect(() => {
      return () => {
        unsubscriber$.next(1);
        unsubscriber$.complete();
        // unsubscriber$.unsubscribe();
      };
    }, [unsubscriber$]);
  
    const handleNext = useCallback((value: unknown) => {
      dispatch({ type: ACTION_STATUS.INTERMEDIATE, payload: value });
    }, []);
  
    const handleFinalize = useCallback(() => {
      dispatch({ type: ACTION_STATUS.LOADED });
    }, []);
  
    /**
     * @function createObserver
     * @description to set status changes inside observer
     */
  
    const createObserver = useCallback(
      () =>
        observerFnRef.current().pipe(
          catchError((error: any) => {
            dispatch({
              type: ACTION_STATUS.ERROR,
              payload: error.response ?? error,
            });
            if (options?.error) {
              options.error(error);
            }
            return of(null);
          }),
          map((data: unknown) => {
            if (data !== null && options?.map) {
              return options?.map(data);
            }
            return data;
          }),
          tap({
            next: handleNext,
          }),
          finalize(handleFinalize)
        ),
      [handleFinalize, handleNext, options]
    );
  
    const reset = useCallback(() => {
      dispatch({ type: ACTION_STATUS.INIT });
    }, [dispatch]);
  
    /**
     * @function subscribe
     * @description to create new subscription to given Asynchronous call and to cancel old subscription in case of changes.
     * @param isStreaming - boolean value to check the given async action is streaming data ( audio, video, live data)
     */
    const subscribe = useCallback(
      (isStreaming = true) => {
        subSink?.unsubscribe();
        dispatch({ type: ACTION_STATUS.INIT });
        dispatch({ type: ACTION_STATUS.LOADING });
        const observer = createObserver();
        subSink.sink = (
          isStreaming
            ? observer
            : observer.pipe(
                take(1),
                shareReplay({
                  bufferSize: 1,
                  refCount: true,
                })
              )
        )
          .pipe(takeUntil(unsubscriber$))
          .subscribe(() => {
            dispatch({ type: ACTION_STATUS.LOADED });
          });
  
        return () => subSink?.unsubscribe();
      },
      [createObserver, subSink, unsubscriber$]
    );
    
  
    const cached = useMemo(
      () => ({ state, subscribe, reset }),
      [state, subscribe, reset]
    );
  
    return cached as AsyncActionInstance<U>;
  }
  
  export const asyncStateMock: AsyncActionState = {
    status: 'INIT',
    init: true,
    loading: false,
    intermediate: false,
    loaded: false,
    error: false,
    value: null,
  };
  